import { useEffect, useRef, useState } from "react";
import { useAppearance } from "../lib/appearance";
import { useAuth } from "../lib/auth-session";
import { ROLES, useRole } from "../lib/roles";

/**
 * Identity, role and sign-out, in a toolbar popover.
 *
 * These used to sit pinned to the bottom of the source list. Apple's sidebar
 * guidance says the opposite: "avoid putting critical information or actions
 * at the bottom of a sidebar" — people often position a window so its bottom
 * edge is off-screen, which is exactly where Sign out was. macOS puts account
 * controls in the toolbar, so that is where they are now.
 */
export function AccountMenu() {
  const { role, setRole } = useRole();
  const { session, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = ROLES.find((r) => r.id === role);
  const label = session.me?.name ?? current?.label ?? "Account";
  const { dark, toggle: toggleTheme } = useAppearance();
  // Two initials for the avatar: "Sara AlJazzar" → "SA", "Khalil" → "K".
  const initials = label.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {/* A 40px circular avatar on --brand-900 with white initials (standard §4). */}
      <button
        type="button"
        className="ui-avatar"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Account: ${label}`}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        {initials}
      </button>

      {open && (
        <div
          className="ui-enter"
          role="dialog"
          aria-label="Account"
          style={{
            position: "absolute", top: "calc(100% + 6px)", insetInlineEnd: 0, zIndex: 50,
            width: 248, padding: 10,
            background: "var(--c-material-popover)",
            border: "1px solid var(--c-border)",
            borderRadius: "var(--ui-sheet-radius)",
            boxShadow: "var(--ui-shadow-popover)",
            display: "grid", gap: 8, textAlign: "start",
          }}
        >
          <button
            type="button"
            className="nl-text-btn"
            style={{ padding: 0, height: "auto", justifySelf: "start" }}
            onClick={toggleTheme}
          >
            {dark ? "Use light appearance" : "Use dark appearance"}
          </button>
          {session.me ? (
            <>
              <div>
                <div style={{ fontWeight: 600, color: "var(--c-t1)", fontSize: "var(--ui-text-body)" }}>
                  {session.me.name}
                </div>
                <div style={{
                  fontSize: "var(--ui-text-meta)", color: "var(--c-t3)",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>{session.me.email}</div>
              </div>
              {current?.hint && (
                <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)" }}>{current.hint}</div>
              )}
              <button
                type="button"
                className="nl-text-btn"
                style={{ padding: 0, height: "auto", justifySelf: "start" }}
                onClick={() => { setOpen(false); void logout(); }}
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <label
                htmlFor="account-role"
                style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t3)" }}
              >
                Viewing as
              </label>
              <select
                id="account-role"
                className="nl-builder-select"
                value={role}
                onChange={(e) => setRole(e.target.value as typeof role)}
              >
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              {current?.hint && (
                <div style={{ fontSize: "var(--ui-text-meta)", color: "var(--c-t4)" }}>{current.hint}</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
